import { effect, inject, Injectable, signal } from '@angular/core';
import { consultant, consultantIT } from './static/plans/consultant';
import { corporate, corporateIT } from './static/plans/corporate';
import { UserService } from './user';
import { LocaleService } from './locale';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  plans = signal({ consultant: consultantIT, corporate: corporateIT });

  // Store Stripe subscription for cancelled but not expired subscriptions
  stripeSubscription = signal<any>(null);

  private userService = inject(UserService);
  private localeService = inject(LocaleService);

  constructor() {
    effect(() => {
      // Read the locale signal to trigger effect when language changes
      const _ = this.localeService.locale();
      if (this.localeService.getLanguage() === 'english') {
        this.plans.set({ consultant, corporate });
      } else {
        this.plans.set({ consultant: consultantIT, corporate: corporateIT });
      }
    });
  }

  getAllPlans = () => {
    if (this.localeService.getLanguage() === 'english') {
      return [...consultant, ...corporate];
    } else {
      return [...consultantIT, ...corporateIT];
    }
  };

  getCurrentPlanDetails = () => {
    const allPlans = this.getAllPlans();
    const user = this.userService.user();

    // @ts-ignore
    if (user?.subscriptionPlan) {
      // @ts-ignore
      return allPlans.find(x => x.id === user.subscriptionPlan);
    } else {
      return null;
    }
  };

  // Get plan details from Stripe subscription (for trialing, cancelled-but-not-expired subscriptions)
  getPlanDetailsFromStripeSubscription = () => {
    const subscription = this.stripeSubscription();
    if (!subscription) return null;

    const isActive = subscription.status === 'active' || subscription.status === 'trialing';
    const isCancelledNotExpired = subscription.cancel_at_period_end && subscription.status === 'active';

    if (isActive || isCancelledNotExpired) {
      const priceId = subscription.items?.data?.[0]?.price?.id ||
                      subscription.items?.data?.[0]?.plan?.id ||
                      subscription.plan?.id;
      if (priceId) {
        const allPlans = this.getAllPlans();
        return allPlans.find((p: any) => p.id === priceId) || null;
      }
    }
    return null;
  };

  // Get effective plan details (from database or Stripe for cancelled subscriptions)
  getEffectivePlanDetails = () => {
    // First try database
    const dbPlan = this.getCurrentPlanDetails();
    if (dbPlan) return dbPlan;

    // Then try Stripe for cancelled but not expired subscriptions
    return this.getPlanDetailsFromStripeSubscription();
  };
}
